import { HighlightSearchTerm } from "components/DesignSystem/HighlightText";
import { InfoSolid } from "components/InfoSolid";
import { ConditionalLink } from "components/conditionalLink";
import { INKLE_PRICING_URL } from "constants/inklePricing";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { useQuery } from "hooks/useQuery";
import { useEffect, useRef } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import CalendarBlank from "static/images/CalendarBlank.svg";
import CoinVertical from "static/images/CoinVertical.svg";
import InfoGray from "static/images/InfoGray.svg";
import ToolTip from "components/design/toolTip";
import { Info } from "components/icons/Info";
import {
  FOREIGN_QUALIFICATION,
  PAY_AS_YOU_GO_ANNUAL_BOOKKEEPING,
  PRO_MONTHLY_BOOKKEEPING,
  STATE_ANNUAL_REPORTS,
  US_BOOKKEEPING_SUBSCRIPTION_PARENT,
  US_BOOKKEEPING_SUBSCRIPTION_SUB_TASK,
} from "types/Models/services";
import { BaseTaskTemplate } from "types/Models/taskTemplate";
import Redirect from "static/images/Redirect.svg";
import classnames from "classnames";
import { useAnalytics } from "hooks/useAnalytics";
import { CLICKED_ON_SERVICE_FROM_INTRAGROUP_SERVICES_LIST_MODAL } from "constants/analyticsEvents";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { Tag } from "components/DesignSystem/Tag/Tag";

dayjs.extend(advancedFormat);

export const TaskDetails = ({
  data,
  currentCategory,
  handleServiceTaskTrigger,
  currentBookkeepingService,
}: {
  data: BaseTaskTemplate & { is_first: boolean };
  currentCategory: string;
  handleServiceTaskTrigger: (taskTemplateId: string) => void;
  currentBookkeepingService?: string;
}) => {
  const query = useQuery();
  const searchTerm = query.get("filing_search") || "";
  const history = useHistory();
  const { url } = useRouteMatch();
  const categoryRef = useRef<HTMLDivElement>(null);
  const { trackEvent } = useAnalytics();
  const isBooksTask =
    data.category === "Bookkeeping" ||
    data.category === "Intragroup" ||
    data.category === "Sales Tax" ||
    data.category === "Bookkeeping Sub Task";
  const { isCustomer } = useRoleBasedView();

  useEffect(() => {
    if (currentCategory === data.category) {
      if (data.is_first) {
        categoryRef?.current?.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
  }, [currentCategory]);

  const disableTask =
    (data.base_task_key == PAY_AS_YOU_GO_ANNUAL_BOOKKEEPING &&
      currentBookkeepingService === PRO_MONTHLY_BOOKKEEPING) ||
    (data.base_task_key == PRO_MONTHLY_BOOKKEEPING &&
      currentBookkeepingService === PAY_AS_YOU_GO_ANNUAL_BOOKKEEPING);

  const handleTaskCardClick = () => {
    trackEvent(CLICKED_ON_SERVICE_FROM_INTRAGROUP_SERVICES_LIST_MODAL, {
      service_category: data.category,
    });
    if (isBooksTask) {
      if (!disableTask) handleServiceTaskTrigger(data.uuid);
      return;
    }
    history.push(`${url}/${data.uuid}`);
  };
  return (
    <div
      aria-details={data.category}
      data-testid={data.base_task_key}
      ref={categoryRef}
      className={classnames(
        "t-mb-3 t-cursor-pointer t-rounded-lg t-p-5 hover:t-shadow-task-templates t-bg-surface-lighter-grey t-flex t-flex-col t-gap-4",
        {
          "t-pointer-events-none t-bg-surface-grey  t-text-neutral-30":
            disableTask,
        }
      )}
      onClick={handleTaskCardClick}
      aria-hidden={true}
    >
      <div className="t-text-subtitle t-text-text-100 t-font-semibold">
        <span>
          <HighlightSearchTerm text={data.title} searchTerm={searchTerm} />
        </span>
        {data.category && (
          <span className="t-inline-block t-ml-2">
            <Tag tagType="purple" icon={false} rounded>
              {data.category}
            </Tag>
          </span>
        )}
      </div>
      <div className="t-space-y-1">
        <div
          className={classnames("t-text-caption t-text-text-30", {
            "t-text-neutral-40": disableTask,
          })}
        >
          About
        </div>
        <span
          className="t-text-body-sm t-text-text-100"
          dangerouslySetInnerHTML={{
            __html:
              data.description?.length > 200
                ? data.description.substring(0, 160) + "....."
                : data.description,
          }}
        ></span>
      </div>
      <div className="t-grid t-grid-cols-2 t-gap-3">
        {data.approx_deadline ? (
          <div className="t-space-y-1">
            <div className="t-text-caption t-text-text-30">
              Typical Deadline
            </div>
            <div className="t-text-body-sm t-text-text-100">
              {dayjs(data.approx_deadline).format("Do MMMM")} each year
            </div>
          </div>
        ) : (
          ""
        )}
        <div className="t-space-y-1">
          <div className="t-text-caption t-text-text-30 t-flex t-items-center t-gap-1">
            Price
            {(data?.base_task_key === STATE_ANNUAL_REPORTS ||
              data?.base_task_key === FOREIGN_QUALIFICATION) && (
              <ToolTip text="Government fees included in this amount">
                <span className="t-text-text-30 t-flex">
                  <Info />
                </span>
              </ToolTip>
            )}
          </div>
          {data?.base_task_key === US_BOOKKEEPING_SUBSCRIPTION_PARENT ||
          data?.base_task_key === US_BOOKKEEPING_SUBSCRIPTION_SUB_TASK ? (
            <div className="t-text-body-sm t-text-text-100">
              {!isCustomer ? "Postpaid" : "$" + data.approx_price}
            </div>
          ) : (
            <div className="t-text-body-sm t-text-text-100">
              {data.approx_price === "0" ? "Free" : "$" + data.approx_price}
            </div>
          )}
        </div>
        {Boolean(data?.government_fee) && (
          <div className="t-space-y-1">
            <div className="t-text-caption t-text-text-30">Government Fee</div>
            <div className="t-text-body-sm t-text-text-100">
              {data.government_fee === "0" ? "Free" : "$" + data.government_fee}
            </div>
          </div>
        )}
      </div>
      {data?.base_task_key === PRO_MONTHLY_BOOKKEEPING && (
        <div className="t-flex t-items-center t-gap-2">
          <div className=" t-text-red">
            <InfoSolid size="16" />
          </div>
          <span className="t-text-body-sm">May have extra charges.</span>
          <ConditionalLink to={INKLE_PRICING_URL} className="t-group">
            <div className="t-flex t-gap-1">
              <span
                className={classnames(
                  "t-text-purple t-text-subtext-sm hover:t-underline",
                  {
                    "t-text-purple-30": disableTask,
                  }
                )}
              >
                Check pricing
              </span>
              <img
                src={Redirect}
                alt="redirect"
                className="group-hover:!t-visible t-invisible t-h-4 t-w-4"
              />
            </div>
          </ConditionalLink>
        </div>
      )}
    </div>
  );
};
